/**
 * AccountTree
 *
 * Renders the full hierarchical account list grouped by account type.
 * Uses a single native <table> so all columns share one layout engine and stay aligned.
 * Balances are fetched for all accounts in accountsMap and rolled up through the tree
 * so parent/header accounts show the sum of their children.
 */

import { useMemo } from 'react'

import { Skeleton } from '@elsapiens/ui'

import {
  CodeCell, NameCell, SubtypeCell, TagsCell, CurrencyCell, StatusCell, ActionsCell,
} from './AccountRow'
import { useAccountBalances, useRolledUpBalances } from './AccountTree-hooks'
import { ColGroup, HeaderRow, GroupHeaderRow, LoadingSkeleton } from './AccountTree-parts'
import { ACCOUNT_TYPES, type AccountRow } from './chart-of-accounts-helpers'
import { formatMoney } from '../../utils/formatters'

import type { GroupRowData } from './AccountTree-parts'
import type { Account, AccountBalance, AccountType } from '../../services/account-service'
import type { Money } from '../../types/common'


// ── Types ─────────────────────────────────────────────────────────────────────

type GroupRow = GroupRowData & { _kind: 'group' }
type AccountFlatRow = AccountRow & { _kind: 'account' }
type FlatRow = GroupRow | AccountFlatRow

interface AccountTreeProps {
  isLoading: boolean
  orgId?: string
  accountsMap: Map<string, Account>
  childrenMap: Map<string, string[]>
  rowsByType: Map<AccountType, AccountRow[]>
  expandedTypes: Set<string>
  baseCurrency: string
  onToggleType: (type: string) => void
  onToggleAccount: (accountId: string) => void
  onEdit: (account: Account) => void
  onToggleActive: (account: Account) => void
  onViewLedger: (accountId: string) => void
}

const COLS = 8

// ── flatRows derivation ───────────────────────────────────────────────────────

function buildFlatRows(rowsByType: Map<AccountType, AccountRow[]>, expandedTypes: Set<string>): FlatRow[] {
  const rows: FlatRow[] = []
  for (const type of ACCOUNT_TYPES) {
    const typeRows = rowsByType.get(type) ?? []
    const isExpanded = expandedTypes.has(type)
    rows.push({ _kind: 'group', id: `group-${type}`, accountType: type, isExpanded, count: typeRows.length })
    if (isExpanded) {
      for (const r of typeRows) {
        rows.push({ ...r, _kind: 'account' })
      }
    }
  }
  return rows
}

// ── Account data row ──────────────────────────────────────────────────────────

interface AccountDataRowProps {
  row: AccountFlatRow
  displayBalance: Money | null
  isFetching: boolean
  hasFetched: boolean
  baseCurrency: string
  onToggleAccount: (accountId: string) => void
  onEdit: (account: Account) => void
  onToggleActive: (account: Account) => void
  onViewLedger: (accountId: string) => void
}

function BalanceCell({ hasFetched, displayBalance }: { hasFetched: boolean; displayBalance: Money | null }): JSX.Element {
  if (!hasFetched) { return <Skeleton className="h-4 w-16 ml-auto" /> }
  if (displayBalance) { return <span>{formatMoney(displayBalance)}</span> }
  return <span className="text-foreground-secondary/50">—</span>
}

function AccountDataRow({
  row, displayBalance, hasFetched, baseCurrency, onToggleAccount, onEdit, onToggleActive, onViewLedger,
}: AccountDataRowProps): JSX.Element {
  return (
    <tr key={row.account.id} className="border-b border-border hover:bg-accent/50 transition-colors">
      <td className="px-3 py-2 pl-4 overflow-hidden"><CodeCell row={row} onToggle={onToggleAccount} /></td>
      <td className="px-3 py-2 overflow-hidden"><NameCell row={row} /></td>
      <td className="px-3 py-2 text-right font-mono text-foreground-secondary">
        <BalanceCell displayBalance={displayBalance} hasFetched={hasFetched} />
      </td>
      <td className="px-3 py-2 overflow-hidden"><SubtypeCell row={row} /></td>
      <td className="px-3 py-2 overflow-hidden"><TagsCell row={row} /></td>
      <td className="px-3 py-2 text-right overflow-hidden"><CurrencyCell baseCurrency={baseCurrency} row={row} /></td>
      <td className="px-3 py-2 overflow-hidden"><StatusCell row={row} /></td>
      <td className="px-3 py-2 pr-4 overflow-hidden">
        <ActionsCell row={row} onEdit={onEdit} onToggleActive={onToggleActive} onViewLedger={onViewLedger} />
      </td>
    </tr>
  )
}

// ── Tree body renderer ────────────────────────────────────────────────────────

interface TreeBodyProps {
  flatRows: FlatRow[]
  rolledUp: Map<string, Money | null>
  balances: Map<string, AccountBalance>
  fetchingRef: React.MutableRefObject<Set<string>>
  baseCurrency: string
  onToggleType: (type: string) => void
  onToggleAccount: (accountId: string) => void
  onEdit: (account: Account) => void
  onToggleActive: (account: Account) => void
  onViewLedger: (accountId: string) => void
}

function TreeBody(props: TreeBodyProps): JSX.Element {
  const { flatRows, rolledUp, balances, fetchingRef, baseCurrency,
    onToggleType, onToggleAccount, onEdit, onToggleActive, onViewLedger } = props
  return (
    <tbody>
      {flatRows.map((row, rowIdx) => {
        if (row._kind === 'group') {
          return <GroupHeaderRow key={row.id} cols={COLS} isFirst={rowIdx === 0} row={row} onToggle={onToggleType} />
        }
        const displayBalance = rolledUp.get(row.account.id) ?? null
        const isFetching = fetchingRef.current.has(row.account.id)
        const hasFetched = balances.has(row.account.id) || !isFetching
        return (
          <AccountDataRow
            key={row.account.id}
            baseCurrency={baseCurrency}
            displayBalance={displayBalance}
            hasFetched={hasFetched}
            isFetching={isFetching}
            row={row}
            onEdit={onEdit}
            onToggleAccount={onToggleAccount}
            onToggleActive={onToggleActive}
            onViewLedger={onViewLedger}
          />
        )
      })}
    </tbody>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AccountTree(props: AccountTreeProps): JSX.Element {
  const { isLoading, orgId, accountsMap, childrenMap, rowsByType, expandedTypes, baseCurrency,
    onToggleType, onToggleAccount, onEdit, onToggleActive, onViewLedger } = props

  const { balances, fetchingRef } = useAccountBalances(orgId, accountsMap)
  const flatRows = useMemo(() => buildFlatRows(rowsByType, expandedTypes), [rowsByType, expandedTypes])
  const rolledUp = useRolledUpBalances(accountsMap, childrenMap, balances, baseCurrency)

  if (isLoading) { return <LoadingSkeleton /> }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
        <ColGroup />
        <HeaderRow />
        <TreeBody
          balances={balances}
          baseCurrency={baseCurrency}
          fetchingRef={fetchingRef}
          flatRows={flatRows}
          rolledUp={rolledUp}
          onEdit={onEdit}
          onToggleAccount={onToggleAccount}
          onToggleActive={onToggleActive}
          onToggleType={onToggleType}
          onViewLedger={onViewLedger}
        />
      </table>
    </div>
  )
}
